import { useTranslation } from 'react-i18next';
import type { Referral } from '@/hooks/useReferrals';
import { SorPicker } from '@/components/patient/ReferralSorPicker';
import { Button } from '@/components/ui/Button';
import { formatReferralDateTime } from '@/lib/referral/referralDateTime';
import { replyEpisodeIdentifier, replyReceiverRule } from '@/lib/referral/referralReply';
import { receivesEpikriseKind, type EpikriseForm } from '@/lib/referral/epikrise';
import { FORM_ERROR_TYPE, FORM_HINT_TYPE } from '@/lib/typography';
import { HEADING, SUB } from '@/components/referrals/epikrisePanelShared';

/**
 * What the epikrise carries over from the received XREF01 (read-only: DIS01-S
 * 4.5/4.28, DIS05-S 4.6–4.8), the receiver (referral rule or SOR, DIS05-S 4.4)
 * and the CC receiver chosen in SOR (DIS0x-S 4.4/4.5).
 */
export function EpikrisePartiesFields({
  referral,
  form,
  update,
  chooseReceiver,
  setChooseReceiver,
  attempted,
  receiverIssue,
  ccIssue,
}: {
  referral: Referral;
  form: EpikriseForm;
  update: (patch: Partial<EpikriseForm>) => void;
  chooseReceiver: boolean;
  setChooseReceiver: (value: boolean) => void;
  attempted: boolean;
  receiverIssue: boolean;
  ccIssue: boolean;
}) {
  const { t } = useTranslation();
  const episode = replyEpisodeIdentifier(referral.medcom_details);
  const receiverRule = replyReceiverRule(referral.medcom_details);
  const address = referral.patient_address ?? {};

  return (
    <>
      <div className={SUB} data-testid="epikrise-from-referral">
        <div className={HEADING}>{t('referrals.epikrise_from_referral', 'Overføres fra henvisningen')}</div>
        <dl className="grid gap-2 text-sm sm:grid-cols-2">
          <div>
            <dt className={FORM_HINT_TYPE}>{t('referrals.epikrise_referral_id', 'Henvisnings-id (Letter/Identifier)')}</dt>
            <dd className="font-mono text-ds-text" data-testid="epikrise-referral-id">
              {referral.medcom_letter_identifier ?? '—'}
            </dd>
          </div>
          <div>
            <dt className={FORM_HINT_TYPE}>{t('referrals.epikrise_referral_received', 'Henvisning modtaget')}</dt>
            <dd className="text-ds-text">{formatReferralDateTime(referral.received_at) ?? '—'}</dd>
          </div>
          <div>
            <dt className={FORM_HINT_TYPE}>{t('referrals.epikrise_referral_diagnosis', 'Henvisningsdiagnose (Refer)')}</dt>
            <dd className="text-ds-text">
              {[referral.referral_diagnosis_code, referral.referral_diagnosis].filter(Boolean).join(' ') || '—'}
            </dd>
          </div>
          <div>
            <dt className={FORM_HINT_TYPE}>{t('referrals.reply_episode', 'Forløbsnummer (EpisodeOfCareIdentifier)')}</dt>
            <dd className="break-all font-mono text-ds-text" data-testid="epikrise-episode">
              {episode.value}
            </dd>
          </div>
          <div>
            <dt className={FORM_HINT_TYPE}>{t('referrals.epikrise_patient', 'Patient')}</dt>
            <dd className="text-ds-text">
              {[referral.first_name, referral.last_name].filter(Boolean).join(' ')}
              {address.streetName ? `, ${address.streetName}` : ''}
              {address.postCodeIdentifier ? `, ${address.postCodeIdentifier}` : ''}
              {address.districtName ? ` ${address.districtName}` : ''}
            </dd>
            <dd className={FORM_HINT_TYPE}>
              {referral.has_cpr
                ? t('referrals.epikrise_cpr_carried', 'CPR-nummer overføres fra henvisningen (vises ikke her).')
                : t('referrals.epikrise_cpr_missing', 'Henvisningen har intet CPR-nummer.')}
            </dd>
          </div>
          <div>
            <dt className={FORM_HINT_TYPE}>{t('referrals.epikrise_occupancy', 'Stilling (OccupancyText)')}</dt>
            <dd className="text-ds-text">{referral.patient_job_title?.trim() || '—'}</dd>
          </div>
        </dl>
      </div>

      <div className={SUB}>
        <div className={HEADING}>{t('referrals.reply_receiver', 'Modtager')}</div>
        {!chooseReceiver ? (
          <>
            <p className="text-sm text-ds-text" data-testid="epikrise-receiver">
              {receiverRule.rule === 'referrer'
                ? t('referrals.reply_receiver_referrer', 'Henviser {{identifier}} ({{code}})', {
                    identifier: receiverRule.identifier,
                    code: receiverRule.identifierCode ?? '—',
                  })
                : t('referrals.reply_receiver_sender', 'Henvisningens afsender (Sender/Identifier)')}
            </p>
            <p className={FORM_HINT_TYPE}>
              {t('referrals.reply_receiver_rule', 'Referrer/Identifier hvis udfyldt, ellers Sender/Identifier (XREF01).')}
            </p>
            <Button type="button" size="sm" variant="secondary" onClick={() => setChooseReceiver(true)}>
              {t('referrals.epikrise_choose_receiver', 'Vælg modtager i SOR')}
            </Button>
          </>
        ) : (
          <>
            <SorPicker
              label={t('referrals.epikrise_receiver_search', 'Modtager (SOR)')}
              fieldId="epikrise-receiver"
              selected={form.receiver}
              partyLabel={t('referrals.reply_receiver', 'Modtager')}
              receives={null}
              hide={(organisation) => !receivesEpikriseKind(form.kind, organisation)}
              onSelect={(organisation) =>
                update({ receiver: { organisation, identifier: organisation.identifier ?? '', chosenIdentifierCode: null } })
              }
              onIdentifier={(identifier) => update({ receiver: form.receiver ? { ...form.receiver, identifier } : null })}
              onIdentifierCode={(chosenIdentifierCode) =>
                update({ receiver: form.receiver ? { ...form.receiver, chosenIdentifierCode } : null })
              }
              onClear={() => update({ receiver: null })}
            />
            {form.kind !== 'XDIS05' && (
              <Button
                type="button"
                size="sm"
                variant="secondary"
                onClick={() => {
                  setChooseReceiver(false);
                  update({ receiver: null });
                }}
              >
                {t('referrals.epikrise_receiver_default', 'Brug henvisningens afsender som modtager')}
              </Button>
            )}
            {attempted && (!form.receiver || receiverIssue) && (
              <p role="alert" className={FORM_ERROR_TYPE}>
                {t(
                  'referrals.epikrise_receiver_missing',
                  'Vælg en modtager med SOR-identifikator (ydernummer eller sygehusafdelingsnummer).',
                )}
              </p>
            )}
          </>
        )}
        <p className={FORM_HINT_TYPE} data-testid="epikrise-sor-note">
          {t(
            'referrals.epikrise_sor_note',
            'Modtagere vælges i SOR-adressebogen ud fra enhedens SOR-identifikator (AfsID: ydernummer eller sygehusafdelingsnummer), ikke ved at indtaste et lokationsnummer; lokationsnummeret følger SOR-enheden. Adressebogen synkroniseres automatisk med SOR hver nat kl. 03.00 dansk tid.',
          )}
        </p>
      </div>

      <div className={SUB}>
        <div className={HEADING}>{t('referrals.epikrise_cc', 'Kopimodtager (CCReceiver), fx egen læge')}</div>
        {referral.answer_cc_receiver?.identifier && (
          <p className={FORM_HINT_TYPE}>
            {t('referrals.epikrise_cc_from_referral', 'Henvisningens kopimodtager af epikrise: {{name}} ({{identifier}}).', {
              name: referral.answer_cc_receiver.organisationName ?? referral.answer_cc_receiver.unitName ?? '—',
              identifier: referral.answer_cc_receiver.identifier,
            })}
          </p>
        )}
        <SorPicker
          label={t('referrals.epikrise_cc_search', 'Kopimodtager (SOR)')}
          fieldId="epikrise-cc"
          selected={form.ccReceiver}
          partyLabel={t('referrals.epikrise_cc_party', 'Kopimodtager')}
          receives={null}
          hide={(organisation) => !receivesEpikriseKind(form.kind, organisation)}
          onSelect={(organisation) =>
            update({ ccReceiver: { organisation, identifier: organisation.identifier ?? '', chosenIdentifierCode: null } })
          }
          onIdentifier={(identifier) => update({ ccReceiver: form.ccReceiver ? { ...form.ccReceiver, identifier } : null })}
          onIdentifierCode={(chosenIdentifierCode) =>
            update({ ccReceiver: form.ccReceiver ? { ...form.ccReceiver, chosenIdentifierCode } : null })
          }
          onClear={() => update({ ccReceiver: null })}
          optional
        />
        {form.ccReceiver && (
          <label className="flex items-center gap-2 text-sm text-ds-text">
            <input
              type="checkbox"
              checked={form.copyToCc}
              onChange={(e) => update({ copyToCc: e.target.checked })}
              data-testid="epikrise-copy-to-cc"
            />
            {t('referrals.epikrise_copy_to_cc', 'Dan samtidig en identisk kopi til kopimodtagerens lokationsnummer')}
          </label>
        )}
        {attempted && ccIssue && (
          <p role="alert" className={FORM_ERROR_TYPE}>
            {t('referrals.epikrise_cc_incomplete', 'Kopimodtageren mangler SOR-identifikator.')}
          </p>
        )}
      </div>
    </>
  );
}
